import * as Api from '@ViewModels';
import { IUser, PostStatus, UserViewModel } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { Switch, useLocation } from 'react-router';
import { toast } from 'sonner';
import { AccountUserSelectBox } from '../../../../admin/components/user/AccountUserSelectBox';
import {
	IImpersonationContextComponentProps,
	ILocationState,
	IModalContext,
	ImpersonationContextKey,
	ModalChildComponentContextKey,
} from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import { EDuplicateType, suppressDuplicateTemplateWarning } from '../../../../models/Campaigns';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import {
	accountHasComplianceEnabled,
	createContentStateWithHtmlStringValue,
	getSocialMediaPlainTextContentFromRawRichContent,
	isValidEmail,
} from '../../../../models/UiUtils';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { shouldShowSendOnBehalfUI } from '../../../../models/permissions/sendOnBehalf';
import {
	invalidateAllTemplateCategorySocialQueries,
	useCreateSocialPostMutation,
	useUpdateTemplateMutation,
} from '../../../../queries';
import { useUsersWithSocialMediaConnection } from '../../../../queries/User/useUsersWithSocialMediaConnection';
import { KnownCategories } from '../../../../viewmodels/AppViewModels';
import { THEME } from '../../../components-v2/Theme/theme';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../components/ConfirmationDialog';
import { ISelectOption } from '../../../components/DeprecatedSelect';
import { FabContext } from '../../../components/FabContext';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { Modal } from '../../../components/Modal';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { PrivateRoute } from '../../../components/PrivateRoute';
import { SaveTemplate } from '../../../components/SaveTemplate';
import { getTemplateApprovalStatus } from '../../../components/TemplateReviewStatusIndicator';
import { TextInputModal } from '../../../components/TextInputModal';
import { PopoverType, TinyPopover } from '../../../components/TinyPopover';
import { UserSelectBox } from '../../../components/UserSelectBox';
import { lastUsedComplianceRecipientEmail } from '../../../components/campaigns/CampaignDetailsSummary';
import { ComplianceConfirmation } from '../../../components/campaigns/ComplianceConfirmation';
import { DuplicateWarningDialog } from '../../../components/campaigns/DuplicateWarningDialog';
import { InlineValueEditor } from '../../../components/entities/InlineValueEditor';
import { SocialMediaPlatformTable } from '../../../components/socialMedia/SocialMediaPlatformTable';
import { SocialMediaPlatformTableType } from '../../../components/socialMedia/SocialMediaPlatformTable/models';
import { SocialMediaPostContentEditor } from '../../../components/socialMedia/SocialMediaPostContentEditor';
import { SocialMediaPostGraphic } from '../../../components/svgs/graphics/SocialMediaPostGraphic';
import { EditItemPenIcon } from '../../../components/svgs/icons/EditItemPenIcon';
import { InfoIcon } from '../../../components/svgs/icons/InfoIcon';
import { LightBulbIcon } from '../../../components/svgs/icons/LightBulbIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { ViewCitationsModal } from '../../../components/templates/ViewCitationsModal';
import { brandPrimaryHover } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { AIScheduleSuccess } from '../../shared/AIScheduleSuccess';
import { CampaignSessionManager, SessionProgressUpdate } from '../../shared/AIScheduleSuccess/campaignSessionManager';
import {
	campaignSessionStore,
	clearGlobalAISourceData,
	shouldOpenAIDrawer,
} from '../../shared/AIScheduleSuccess/campaignSessionStore';
import { SocialMediaApprovalSetSchedule } from '../../socialMediaPostApproval/SocialMediaApprovalSetSchedule';
import { UniversalAssistantChatWithinComposer } from '../../templates/AssistantChat';
import { EditSocialMediaSendOptions } from '../../templates/EditSocialMediaSendOptions';
import { CopyPreviewLink } from '../CopyPreviewLink';
import { SocialMediaPostScheduleComplete } from '../SocialMediaPostScheduleComplete';
import { EditSocialMediaPostContext } from './context';
import { useEditSocialMediaPost } from './hooks';
import { MobileEditSocialMediaPostContainer } from './mobile';
import { CancelConfirmationOptions, CancelUpdateConfirmationOptions, UpdateConfirmationOptions } from './models';
import { UnsavedChangesWarning } from '../../../components-v2/UnsavedChangesWarning';
import { DefaultComposerContext, SocialMediaNavigation, StatusSpecificDetails } from './presentation';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps, IModalContext {
	className?: string;
	onPostUpdated?(): boolean;
	styles?: StyleDeclarationValue[];
}

const EditSocialMediaPostBase: React.FC<IProps> = observer(props => {
	const { className, styles = [], impersonationContext, parentModal, onPostUpdated } = props;

	const {
		context,
		duplicateType,
		errorMessages,
		h,
		impersonationCtx,
		initialSelectedUserOptionRef,
		isLoaded,
		isTestimonialMode,
		locationStateCreateRequest,
		logApiError,
		postReport,
		postToEdit,
		routeMatch,
		scheduledPost,
		setScheduledPost,
		setShowDuplicateWarning,
		setSocialMediaConnections,
		showDuplicateWarning,
		socialMediaConnections,
		source,
		suggestionId,
		templateId,
		userIsLoaded,
	} = useEditSocialMediaPost({ impersonationContext });
	const location = useLocation();
	const { postNotification } = useLocalNotificationService<Api.SocialMediaPostViewModel>();
	const userSession = useUserSession();
	const usersWithSocial = useUsersWithSocialMediaConnection({
		transformer: users => users.map(user => new UserViewModel(userSession, user)),
		userSession,
	});
	const fullscreenModal = useFullscreenModal();

	const handleCampaignSessionNavigation = React.useCallback(
		(progress?: SessionProgressUpdate | null, socialPost?: Api.SocialMediaPostViewModel) => {
			if (progress?.isCampaignComplete) {
				CampaignSessionManager.clearSession();
				fullscreenModal.dismissModal();
				return;
			}

			const sessionId = progress?.session?.id ?? CampaignSessionManager.getSession()?.id;
			const locationState = sessionId ? { socialPost, sessionId } : { socialPost };

			const pathname = `${routeMatch.url}/schedule-complete`;
			fullscreenModal.history.replace({
				pathname,
				state: locationState,
			});
		},
		[fullscreenModal, routeMatch.url]
	);

	const [showingAIAssistant, setShowingAIAssistant] = React.useState(() => {
		const shouldOpen = shouldOpenAIDrawer();
		if (shouldOpen) {
			clearGlobalAISourceData();
		}
		return shouldOpen;
	});

	const sourceContent = campaignSessionStore.aiSourceContent;
	const sourceType = campaignSessionStore.aiSourceType;

	const [showSaveAsModal, setShowSaveAsModal] = React.useState(false);
	const [templateToBeSaved, setTemplateToBeSaved] = React.useState<Api.ITemplate | null>(null);

	const readOnly = source ? !source.canEdit : false;

	const [isSchedulingInProgress, setIsSchedulingInProgress] = React.useState(false);

	const [bypassCompliance, setBypassCompliance] = React.useState(false);
	const [editableCampaignName, setEditableCampaignName] = React.useState<string>(null);
	const [isEditingName, setIsEditingName] = React.useState(false);

	const isComplianceFlow = accountHasComplianceEnabled(userSession, impersonationContext);

	const [sendWithComplianceEmail, setSendWithComplianceEmail] = React.useState<string>(() => {
		return localStorage.getItem(lastUsedComplianceRecipientEmail) ?? '';
	});

	const previewLink = source?.anonymousAccessLink || scheduledPost?.anonymousAccessLink || '';

	const onSelectedConnectionsChanged = React.useCallback(
		(selected: Api.IPostTarget[]) => {
			context.setPostTargets(selected);
			context.setHasChanges(true);
		},
		[context]
	);

	const onOverlayLogoChange = React.useCallback(async () => {
		if (!impersonationCtx || !readOnly) {
			return;
		}

		const postToUpdate = scheduledPost || postToEdit;

		if (!postToUpdate) {
			return;
		}

		try {
			const [, error] = await context.updatePost(postToUpdate);

			if (error) {
				logApiError('OverlayLogoAutoSave-Error', error);
				errorMessages.pushApiError(error);
				toast.error('Failed to save overlay logo change.');
				return;
			}

			context.setHasChanges(false);
			toast.success('Overlay logo saved.');
		} catch (err) {
			logApiError('OverlayLogoAutoSave-Error', err);
			errorMessages.pushApiError(err);
			toast.error('Failed to save overlay logo change.');
		}
	}, [context, errorMessages, impersonationCtx, logApiError, postToEdit, readOnly, scheduledPost]);

	const reloadSource = React.useCallback(async () => {
		try {
			await source.impersonate(impersonationCtx)?.load();
		} catch (err) {
			logApiError('ReloadPostReport-Error', err);
			toast.error('Report failed to reload. Please refresh the page to see the latest status.');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [source]);

	const handlePostNow = (e?: React.MouseEvent<HTMLElement>) => {
		onSubmitPost(e, moment());
	};

	const handleScheduleWithoutCompliance = (e?: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		setBypassCompliance(true);
		h.push(`${routeMatch.url}/send-options`);
	};

	const checkComplianceEmail = () => {
		if (sendWithComplianceEmail.length > 0 && !isValidEmail(sendWithComplianceEmail)) {
			toast.error(`Please enter a valid email address`);
			return false;
		}
		return true;
	};

	const handleScheduleForLater = (e?: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		if (isComplianceFlow && !checkComplianceEmail()) {
			return;
		}
		h.push(`${routeMatch.url}/send-options`);
	};

	const handleChangeDate = async () => {
		if (postReport) {
			await reloadSource();
		}
		h.push(`${routeMatch.url}/set-social-schedule`);
	};

	const onCancelPost = React.useCallback(async () => {
		if (scheduledPost) {
			try {
				await scheduledPost.cancel();
				postNotification({
					info: scheduledPost,
					topic: Topics.EDIT_SOCIAL_POST,
				});
				CampaignSessionManager.clearSession();
				parentModal.onRequestClose();
			} catch (err) {
				errorMessages.pushApiError(err);
				logApiError('CancelPost-Error', err);
			}
		}
	}, [scheduledPost, logApiError, errorMessages, parentModal, postNotification]);

	const [showingCancelConfirmation, setShowingCancelConfirmation] = React.useState(false);
	const [showingCancelUpdateConfirmation, setShowingCancelUpdateConfirmation] = React.useState(false);
	const [showingUpdateConfirmation, setShowingUpdateConfirmation] = React.useState(false);
	const [showingCitations, setShowingCitations] = React.useState(false);

	const onShowCancelConfirmation = (e?: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();

		setShowingCancelConfirmation(true);
	};

	const onShowCancelUpdateConfirmation = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		setShowingCancelUpdateConfirmation(true);
	};

	const onRequestClose = () => {
		CampaignSessionManager.clearSession();
		parentModal?.onRequestClose();
	};

	const onShowUpdateConfirmation = () => {
		setShowingUpdateConfirmation(true);
	};

	const onCancelConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			onCancelPost();
		}
		setShowingCancelConfirmation(false);
	};

	const onCancelUpdateConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			CampaignSessionManager.clearSession();
			parentModal?.onRequestClose();
		}
		setShowingCancelUpdateConfirmation(false);
	};

	const onCitationRequestClose = () => {
		setShowingCitations(false);
	};

	const onUpdateConfirmationRequestClose = async (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			const selectedSendTime =
				postToEdit?.status === Api.PostStatus.Draft || scheduledPost?.status === Api.PostStatus.Draft
					? undefined
					: moment(postReport?.dueDate || postToEdit?.dueDate);
			await onSubmitPost(undefined, selectedSendTime);
			postNotification({
				info: scheduledPost,
				topic: Topics.EDIT_SOCIAL_POST,
			});
		}
		setShowingUpdateConfirmation(false);
	};

	const onSaveAsButtonClicked = () => {
		setShowSaveAsModal(true);
	};

	const createSocialPostMutation = useCreateSocialPostMutation({
		onSuccess: async data => {
			const socialMediaPostVm = new Api.SocialMediaPostViewModel(userSession, data);
			postNotification({
				info: socialMediaPostVm,
				topic: Topics.CREATE_SOCIAL_POST,
			});
			toast.success('Your post has been saved as a draft.');

			try {
				await onPostScheduled(socialMediaPostVm);
				locationStateCreateRequest?.onFinish?.(true);
				if (onPostUpdated?.()) {
					return;
				}
				const locationState: ILocationState<Api.SocialMediaPostViewModel, null> = {
					viewModel: socialMediaPostVm,
				};
				fullscreenModal.history.replace({
					pathname: `${routeMatch.url}`,
					state: locationState,
				});
			} finally {
				setIsSchedulingInProgress(false);
			}
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('CreateSocialPost', error);
			setIsSchedulingInProgress(false);
		},
	});
	const onSaveAsDraftPost = async (newCampaignName?: string, cancel?: boolean) => {
		if (!cancel && newCampaignName) {
			setIsSchedulingInProgress(true);
			context.setCampaignName(newCampaignName);
			const post: Api.ICreateSocialMediaPostRequest = {
				...context.getPostBase(),
				forUserId: context.selectedCreator?.id,
				sendWithCompliance:
					impersonationContext?.account?.preferences?.complianceSettings?.enabled ||
					userSession?.account?.preferences?.complianceSettings?.enabled,
				sendWithComplianceEmail,
				name: newCampaignName,
			};
			createSocialPostMutation.mutate({
				request: post,
				forUserId: post?.forUserId,
				contentCalendarSuggestionId: suggestionId,
				impersonationContext,
			});
		}
		setShowSaveAsModal(false);
	};

	const onPostScheduled = React.useCallback(
		async (postViewModel: Api.SocialMediaPostViewModel, switchToDraftEditor = false) => {
			if (postViewModel) {
				await postViewModel.impersonate(impersonationCtx).load();
				setScheduledPost(postViewModel);
				context.setCampaignName(postViewModel.name);
				context.setHasChanges(true);

				if (
					switchToDraftEditor &&
					!fullscreenModal?.history?.location?.pathname?.includes('/social/edit') &&
					!impersonationContext?.isValid
				) {
					fullscreenModal?.history?.replace({
						pathname: `/social-media/post/edit-post-draft/${postViewModel.id}`,
						state: {
							viewModel: postViewModel,
						},
					});
				}
			}
		},
		[context, fullscreenModal?.history, impersonationContext?.isValid, impersonationCtx, setScheduledPost]
	);

	const onSubmitPost = React.useCallback(
		async (_: React.MouseEvent<HTMLElement>, selectedSendTime?: Moment, ignoreCompliance?: boolean) => {
			if (isSchedulingInProgress) {
				return;
			}
			setIsSchedulingInProgress(true);
			const { schedulePost, updatePost } = context;
			try {
				if (postReport || postToEdit || scheduledPost) {
					const complianceEmail = isComplianceFlow && !ignoreCompliance ? sendWithComplianceEmail : undefined;
					const [, error] = await updatePost(scheduledPost, selectedSendTime, ignoreCompliance, complianceEmail);
					if (error) {
						logApiError('UpdateSocialMediaPost-Error', error);
						errorMessages.pushApiError(error);
						return;
					}

					if (source) {
						reloadSource();
					}

					if (postToEdit) {
						try {
							await postToEdit.load();
						} catch (err) {
							logApiError('UpdateSocialMediaPost-Error', err);
							errorMessages.pushApiError(err);
							return;
						}
					}

					toast.success(
						!selectedSendTime
							? 'Your draft has been updated.'
							: selectedSendTime.isSame(moment(), 'hour')
								? 'Your post has been successfully sent.'
								: postToEdit?.status === Api.PostStatus.Pending
									? 'Your post has been successfully updated.'
									: 'Your post has been successfully scheduled.'
					);

					if (onPostUpdated?.()) {
						return;
					}

					if (isComplianceFlow && !ignoreCompliance && selectedSendTime) {
						h.replace(`${routeMatch.url}/compliance-complete`);
					} else if (selectedSendTime) {
						const hasCampaignSession = CampaignSessionManager.hasActiveSession();
						const isSendNow = selectedSendTime.diff(moment(), 'minutes') <= 10;
						const isPrimarySendNow = CampaignSessionManager.isPrimaryResourceSendNow('social', isSendNow);
						const scheduledDate = selectedSendTime.toDate();

						if (hasCampaignSession) {
							const socialContext = CampaignSessionManager.createAIContextFromSocialData(postToEdit || scheduledPost);
							const progress = socialContext
								? CampaignSessionManager.updateSessionProgress('social', socialContext, scheduledDate)
								: null;

							// If campaign is complete or it's send now, dismiss modal
							if (progress?.isCampaignComplete || isPrimarySendNow) {
								CampaignSessionManager.clearSession();
								fullscreenModal.dismissModal();
								return;
							}

							handleCampaignSessionNavigation(progress, postToEdit || scheduledPost);
							return; // Exit early to prevent fallthrough
						}

						// Only check these conditions if no campaign session
						if (isPrimarySendNow || isTestimonialMode) {
							CampaignSessionManager.clearSession();
							fullscreenModal.dismissModal();
						} else {
							fullscreenModal.history.replace({
								pathname: `${routeMatch.url}/schedule-complete`,
								state: { socialPost: postToEdit || scheduledPost },
							});
						}
					} else {
						const locationState: ILocationState<Api.SocialMediaPostViewModel, null> = {
							viewModel: postToEdit,
						};
						fullscreenModal.history.replace({
							pathname: `${routeMatch.url}`,
							state: locationState,
						});
					}
					postNotification({
						info: postToEdit,
						topic: Topics.EDIT_SOCIAL_POST,
					});
				} else {
					const hadCampaignSession = CampaignSessionManager.hasActiveSession();
					const [postViewModel, error] = await schedulePost(
						selectedSendTime,
						suggestionId,
						impersonationCtx,
						ignoreCompliance,
						sendWithComplianceEmail
					);
					if (error) {
						logApiError('CreateSocialMediaPost-Error', error);
						errorMessages.pushApiError(error);
						return;
					}

					toast.success(
						selectedSendTime.isSame(moment(), 'hour')
							? 'Your post has been successfully sent.'
							: 'Your post has been successfully scheduled.'
					);

					// Update campaign session - use startSession to properly handle primary vs secondary resource logic
					const aiContext = CampaignSessionManager.createAIContextFromSocialData(postViewModel);
					let sessionProgress: SessionProgressUpdate | null = null;
					const scheduledDate = postViewModel.dueMoment ? postViewModel.dueMoment.toDate() : selectedSendTime?.toDate();

					if (aiContext) {
						if (hadCampaignSession) {
							sessionProgress = CampaignSessionManager.updateSessionProgress('social', aiContext, scheduledDate);
						} else {
							CampaignSessionManager.startSession('social', aiContext, scheduledDate);
						}
					}

					await onPostScheduled(postViewModel);
					locationStateCreateRequest?.onFinish?.(true);

					const postUpdatedResult = onPostUpdated?.();

					if (postUpdatedResult) {
						return;
					}

					if (isComplianceFlow && !ignoreCompliance && selectedSendTime) {
						h.replace(`${routeMatch.url}/compliance-complete`);
					} else {
						const isSendNow = selectedSendTime.diff(moment(), 'minutes') <= 10;
						const isPrimarySendNow = CampaignSessionManager.isPrimaryResourceSendNow('social', isSendNow);

						// Check if there's currently an active session after session updates
						const hasActiveCampaignSession = CampaignSessionManager.hasActiveSession();

						if (hasActiveCampaignSession) {
							// If campaign is complete or it's send now for primary with no prior session, dismiss modal
							if (sessionProgress?.isCampaignComplete || (!hadCampaignSession && isPrimarySendNow)) {
								CampaignSessionManager.clearSession();
								fullscreenModal.dismissModal();
								return;
							}

							handleCampaignSessionNavigation(sessionProgress, postViewModel);
							return; // Exit early
						}

						// Only handle non-session cases
						if (isPrimarySendNow || isTestimonialMode) {
							CampaignSessionManager.clearSession();
							fullscreenModal.dismissModal();
						} else {
							fullscreenModal.history.replace({
								pathname: `${routeMatch.url}/schedule-complete`,
								state: { socialPost: postViewModel },
							});
						}
					}
					postNotification({
						info: postViewModel,
						topic: Topics.CREATE_SOCIAL_POST,
					});
				}
			} finally {
				context.setHasChanges(false);
				setIsSchedulingInProgress(false);
			}
		},
		[
			context,
			errorMessages,
			fullscreenModal,
			h,
			handleCampaignSessionNavigation,
			impersonationCtx,
			isComplianceFlow,
			isSchedulingInProgress,
			isTestimonialMode,
			locationStateCreateRequest,
			logApiError,
			onPostScheduled,
			onPostUpdated,
			postNotification,
			postReport,
			postToEdit,
			reloadSource,
			routeMatch.url,
			scheduledPost,
			sendWithComplianceEmail,
			source,
			suggestionId,
		]
	);
	const onViewCitationsClicked = async () => {
		setShowingCitations(true);
	};

	const onUserOptionSelected = async (option: ISelectOption<UserViewModel<IUser>>, user?: UserViewModel<IUser>) => {
		initialSelectedUserOptionRef.current = option;
		if (impersonationCtx && option?.dataContext) {
			impersonationCtx.user = { id: user?.id };
			user.impersonate(impersonationCtx);
		}
		if (
			(!option.dataContext.socialMediaConnectedAccounts ||
				option.dataContext.socialMediaConnectedAccounts?.length === 0) &&
			user
		) {
			try {
				await user.load();
				context.setSelectedCreator(user.toJs());
			} catch (err) {
				errorMessages.pushApiError(err);
				logApiError('LoadUser-Error', err);
			}
		} else if (
			(!option.dataContext.socialMediaConnectedAccounts ||
				option.dataContext.socialMediaConnectedAccounts?.length === 0) &&
			!user
		) {
			try {
				await option.dataContext.load();
				context.setSelectedCreator(option.dataContext.toJs());
			} catch (err) {
				errorMessages.pushApiError(err);
				logApiError('LoadUser-Error', err);
			}
		} else {
			context.setSelectedCreator(option.dataContext);
		}
		context.setHasChanges(true);
		const connections =
			option.dataContext.socialMediaConnectedAccounts?.filter(
				x =>
					(x.state === Api.SocialMediaConnectionState.Connected ||
						x.state === Api.SocialMediaConnectionState.Disconnected) &&
					!!x.postTargetId
			) ||
			user?.socialMediaConnectedAccounts?.filter(
				x =>
					(x.state === Api.SocialMediaConnectionState.Connected ||
						x.state === Api.SocialMediaConnectionState.Disconnected) &&
					!!x.postTargetId
			) ||
			[];
		setSocialMediaConnections(
			connections?.filter(x => x.state === Api.SocialMediaConnectionState.Connected && !!x.postTargetId)
		);

		// Update below to account for locationStateCreateRequest
		context.setPostTargets(
			connections
				?.filter(
					x =>
						x.state === Api.SocialMediaConnectionState.Connected &&
						x?.postTargetId &&
						(!locationStateCreateRequest ||
							!locationStateCreateRequest?.targets?.length ||
							locationStateCreateRequest?.targets?.includes(x.type))
				)
				.map(y => ({ pageId: y.postTargetId, provider: y.type, userId: y.userId })) || []
		);
	};

	const onAIContentGenerationAssistantButtonClicked = () => {
		setShowingAIAssistant(true);
	};

	const IGNORE_EXPIRED_DATE = [
		Api.PostStatus.Draft,
		Api.PostStatus.Succeeded,
		Api.PostStatus.PartiallySucceeded,
		Api.PostStatus.Started,
		Api.PostStatus.Cancelled,
		Api.PostStatus.Failed,
	];

	const expirationDateInEffect =
		context?.template?.schedule?.expirationDate && !IGNORE_EXPIRED_DATE.includes(source?.status);

	const handleOnRequestClose = (suppress?: boolean, cancel?: boolean) => {
		if (suppress) {
			suppressDuplicateTemplateWarning(templateId);
		}
		if (cancel) {
			setShowDuplicateWarning(false);
			fullscreenModal?.dismissModal();
			return;
		}
		setShowDuplicateWarning(false);
	};

	const onEditNameCancelButtonClicked = () => {
		setIsEditingName(false);
	};

	const onEditNameValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setEditableCampaignName(value);
	};

	const onEditNameSaveButtonClicked = () => {
		context.setCampaignName(editableCampaignName);
		context.setHasChanges(true);
		setIsEditingName(false);
	};

	const onEditNameButtonClicked = () => {
		setIsEditingName(true);
	};

	const renderDefaultComposerContext = () => {
		return (
			<DefaultComposerContext
				context={context}
				impersonationCtx={impersonationCtx}
				expirationDateInEffect={expirationDateInEffect}
				onRenderStatusSpecificDetails={() => {
					return (
						<StatusSpecificDetails
							postToEdit={postToEdit}
							scheduledPost={scheduledPost}
							context={context}
							isComplianceFlow={isComplianceFlow}
							sendWithComplianceEmail={sendWithComplianceEmail}
							setSendWithComplianceEmail={setSendWithComplianceEmail}
							isSchedulingInProgress={isSchedulingInProgress}
							impersonationCtx={impersonationCtx}
							handleScheduleForLater={handleScheduleForLater}
							handleScheduleWithoutCompliance={handleScheduleWithoutCompliance}
							handlePostNow={handlePostNow}
							checkComplianceEmail={checkComplianceEmail}
							lastUsedComplianceRecipientEmail={lastUsedComplianceRecipientEmail}
							handleChangeDate={handleChangeDate}
							onViewCitationsClicked={onViewCitationsClicked}
							source={source}
							onOverlayLogoChange={onOverlayLogoChange}
						/>
					);
				}}
				overlayLogo={context.overlayLogo}
				hideWhiteBackground={context.hideWhiteBackground}
				logoSize={context.logoSize}
			/>
		);
	};
	const postContentState = React.useMemo(
		() => createContentStateWithHtmlStringValue(context.postContent).getRawRichTextContent(),
		[context.postContent]
	);
	const onSaveAsTemplateClicked = () => {
		const newTemplate: Api.ITemplate = {
			...context?.template,
			attachments: context.postImages,
			content: postContentState,
			name: context.campaignName,
			scope: context?.template?.scope,
			templateType: Api.TemplateType.SocialMediaPost,
		};
		delete newTemplate.id;
		setTemplateToBeSaved(newTemplate);
	};

	const onForkedTemplate = () => {
		setTemplateToBeSaved(null);
		invalidateAllTemplateCategorySocialQueries();

		// Don't redirect if we're in admin context (admin URLs contain '/admin' or start with admin routes)
		const isAdminContext = window.location.pathname.includes('/admin') || window.location.hash.includes('/accounts/');

		if (!isAdminContext) {
			h.push(`/campaigns/social?category=${KnownCategories.MyTemplates}`);
		}
	};
	const updateTemplateMutation = useUpdateTemplateMutation({
		onSuccess: template => {
			toast.success('Template saved successfully');
			context.setTemplate(template);
			invalidateAllTemplateCategorySocialQueries();
			setShowingUpdateConfirmation(postReport != null || postToEdit != null || scheduledPost != null);
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('ErrorSavingSocialTemplate', error);
		},
	});
	const handleSave = () => {
		const template: Api.ITemplate = {
			...context?.template,
			attachments: context.postImages,
			content: postContentState,
			name: context.campaignName,
			scope: context?.template?.scope,
		};
		updateTemplateMutation.mutate({ template });
	};

	const canSaveTemplate =
		(context.template?.scope === Api.TemplateScope.User || context.template?.scope === Api.TemplateScope.Account) &&
		(context.template?.creator?.id === userSession.user.id || userSession.userRole.includes('admin'));

	const onRenderComposer = () => {
		if (!isLoaded || !userIsLoaded) {
			return <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />;
		}
		const isUsingAccountImpersonation = !!impersonationCtx;
		const canSendOnBehalf = shouldShowSendOnBehalfUI({
			isUsingAccountImpersonation,
			userRole: userSession?.userRole,
			sendOnBehalfEnabled: userSession?.account?.preferences?.sendOnBehalfEnabled,
		});

		const newPost = !source && !scheduledPost;
		const heading = !templateId && !source && !context.campaignName ? 'New Post' : context.campaignName;
		let socialMediaTableType: SocialMediaPlatformTableType = SocialMediaPlatformTableType.Reporting;
		socialMediaTableType = (!source || (source.canEdit && !impersonationCtx)) && SocialMediaPlatformTableType.Mutable;
		socialMediaTableType =
			((source && impersonationCtx) || (source && !source?.canEdit)) && SocialMediaPlatformTableType.ReadOnly;

		let socialUserOptions: ISelectOption<string | UserViewModel>[] = [];
		if (usersWithSocial?.data?.length > 0) {
			socialUserOptions = usersWithSocial.data
				.filter(x => x.id !== userSession?.user?.id)
				.filter(x => x.id !== context.selectedCreator?.id)
				.map<ISelectOption>(x => {
					return {
						dataContext: x,
						id: `option-${x.id}-social`,
						text: `${x.firstName} ${x.lastName}`,
					};
				});
		}
		return (
			<>
				<div className={css(styleSheet.composerContainer)}>
					<div className={css(styleSheet.composerContainerHeader)}>
						<div className={css(styleSheet.nameContainer, baseStyleSheet.truncateText)}>
							<div className={css(styleSheet.headingEditor)}>
								{!isEditingName ? (
									<div className={css(styleSheet.heading)}>{heading}</div>
								) : (
									<InlineValueEditor
										autoComplete='off'
										autoFocus={true}
										className={`inline-value-editor ${css(styleSheet.headingEditorInput)}`}
										id='post-name-editor-input'
										onCancelButtonClicked={onEditNameCancelButtonClicked}
										onChange={onEditNameValueChanged}
										onSaveButtonClicked={onEditNameSaveButtonClicked}
										value={editableCampaignName || context.campaignName || 'New Post'}
									/>
								)}
								{!isEditingName && !readOnly ? (
									<button disabled={false} onClick={onEditNameButtonClicked}>
										<EditItemPenIcon className={css(styleSheet.editIcon)} />
									</button>
								) : null}
								{getTemplateApprovalStatus(context?.template?.templateReview)}
							</div>
							<div className={css(styleSheet.buttonContainer)}>
								{!impersonationCtx || source ? <CopyPreviewLink anonymousAccessLink={previewLink} /> : null}

								{context?.template?.citation?.length > 0 ? (
									<button
										className={css(baseStyleSheet.ctaButtonReverseSmall, styleSheet.citationButton)}
										onClick={onViewCitationsClicked}
									>
										<span>View Citations</span>
									</button>
								) : null}
							</div>
						</div>
						{newPost && context?.template?.summary?.length > 0 ? (
							<div className={css(styleSheet.summary)}>
								<LightBulbIcon className={css(styleSheet.lightBulb)} fill='#00aae8' />
								<div>{context?.template?.summary}</div>
							</div>
						) : null}
						{newPost && canSendOnBehalf ? (
							<div className={css(styleSheet.postFrom)}>
								<span>Post From:</span>
								<div className={css(styleSheet.userDropdown)}>
									{impersonationCtx ? (
										<AccountUserSelectBox
											defaultOption={null}
											onOptionSelected={onUserOptionSelected}
											onRenderPlaceholder={() => <span>Select User</span>}
											adminsFilter={x => x.socialMediaConnectedAccounts?.length > 0}
											initialSelectedOption={initialSelectedUserOptionRef.current}
										/>
									) : (
										<UserSelectBox
											defaultOption={null}
											includeSearch={false}
											onOptionSelected={onUserOptionSelected}
											onRenderPlaceholder={() => <span>Select User</span>}
											initialSelectedOption={initialSelectedUserOptionRef.current}
											additionalPinnedOptions={socialUserOptions}
										/>
									)}
								</div>
								<TinyPopover
									anchor={
										<figure className={css(baseStyleSheet.flex, baseStyleSheet.itemsCenter)}>
											<InfoIcon className={css(styleSheet.infoIcon)} fillColor={brandPrimaryHover} />
										</figure>
									}
									className={css(styleSheet.infoPopover)}
									dismissOnOutsideAction={true}
									toggleOnHover={true}
									autoCloseOtherPopoversOnHover={true}
									type={PopoverType.background}
									placement={['right', 'top', 'bottom']}
								>
									<p className={css(styleSheet.popoverContent)}>Users with connected social media accounts.</p>
								</TinyPopover>
							</div>
						) : null}
					</div>
					{!readOnly ? (
						<SocialMediaNavigation
							onAIContentGenerationAssistantButtonClicked={onAIContentGenerationAssistantButtonClicked}
							canSaveTemplate={canSaveTemplate}
							updateTemplateMutation={updateTemplateMutation}
							handleSave={handleSave}
							onSaveAsTemplateClicked={onSaveAsTemplateClicked}
							newPost={newPost}
							impersonationCtx={impersonationCtx}
							context={context}
							onSaveAsButtonClicked={onSaveAsButtonClicked}
							source={source}
							onShowCancelConfirmation={onShowCancelConfirmation}
							onShowUpdateConfirmation={onShowUpdateConfirmation}
						/>
					) : null}
					<UnsavedChangesWarning
						hasUnsavedChanges={!!context?.hasChanges}
						message='You have unsaved changes to this post.'
					/>
					<div className={css(styleSheet.composerContainerBody)}>
						{locationStateCreateRequest?.sendFromUser?.id === userSession.user.id || context?.selectedCreator ? (
							<div>
								<div className={css(styleSheet.whereToShare, styleSheet.whereToShareAdmin)}>
									<p>Where to Share</p>
								</div>
								{socialMediaConnections?.length ? (
									<SocialMediaPlatformTable
										connections={socialMediaConnections}
										onSelectionChanged={onSelectedConnectionsChanged}
										source={source}
										type={socialMediaTableType}
									/>
								) : (
									<div className={css(styleSheet.noAccountsWarning)}>
										Selected user is currently not linked to any social media accounts.
									</div>
								)}
							</div>
						) : null}
						<SocialMediaPostContentEditor
							imagesLabel='Add Media'
							messageLabel='Content'
							impersonationContext={impersonationContext}
							onPostScheduled={vm => onPostScheduled(vm, true)}
							post={postToEdit || scheduledPost}
							readOnly={readOnly && scheduledPost?.status !== PostStatus.Draft}
						/>
					</div>
				</div>
				{!showingAIAssistant ? (
					<div className={css(styleSheet.contextContainer)}>
						{context.postContent || context?.postImages?.length > 0 ? (
							renderDefaultComposerContext()
						) : (
							<div className={css(styleSheet.graphicContainer)}>
								<div className={css(styleSheet.sloganContainer)}>Let&apos;s create a post</div>
								<SocialMediaPostGraphic />
							</div>
						)}
					</div>
				) : null}
				{/* We handle conitional rendering within the component, so we can manage the chat session. Don't wrap this in a conditional. */}
				<UniversalAssistantChatWithinComposer
					isOpen={showingAIAssistant}
					onUseSocialContent={(content, aiReference) => {
						context.setContent(getSocialMediaPlainTextContentFromRawRichContent(content));
						context.setFacebookMentions([]);
						context.setAIReference(aiReference);
					}}
					socialContext={{
						content: postContentState,
					}}
					emailContext={sourceType === 'email' && sourceContent ? (sourceContent as Api.EmailAIContext) : undefined}
					blogContext={sourceType === 'blog' && sourceContent ? (sourceContent as Api.BlogAIContext) : undefined}
					impersonationContext={impersonationContext?.toJs()}
					onClose={() => setShowingAIAssistant(false)}
				/>
			</>
		);
	};

	const onModalCloseWithUnsavedChangesWarning = (e: React.MouseEvent<HTMLElement>) => {
		if (context?.hasChanges) {
			onShowCancelUpdateConfirmation(e);
			return;
		}
		onRequestClose();
	};

	const onBackButtonClicked = (e: React.MouseEvent<HTMLElement>) => {
		if (location.pathname === '/social-media/post/create') {
			e.preventDefault();
			onModalCloseWithUnsavedChangesWarning(e);
			return;
		}
	};
	return (
		<div className={`${css(styleSheet.container, ...styles)} edit-social-media-post ${className || ''}`}>
			<MultiContainerHeader
				fullscreenHeader={
					!postReport && !postToEdit && !scheduledPost
						? 'Create Post'
						: readOnly || (scheduledPost && !postReport)
							? 'Viewing Post'
							: 'Edit Post'
				}
				onFullscreenRequestClose={onModalCloseWithUnsavedChangesWarning}
				onFullscreenRequestBack={onBackButtonClicked}
			/>
			<DuplicateWarningDialog
				isOpen={showDuplicateWarning}
				onRequestClose={handleOnRequestClose}
				title='Social Media Post Duplicate'
				type={CampaignType.Social}
				duplicateType={duplicateType}
				lastUsedDate={
					duplicateType === EDuplicateType.Account
						? context?.template?.lastUsedByDate?.account
						: context?.template?.lastUsedByDate?.currentUser
				}
			/>
			<EditSocialMediaPostContext.Provider value={context}>
				<Switch>
					<PrivateRoute exact={false} path={`${routeMatch.url}/set-social-schedule`} userSession={userSession}>
						<SocialMediaApprovalSetSchedule
							postVm={scheduledPost}
							sendOnBehalf={
								(context?.selectedCreator?.id !== userSession.user.id && !impersonationContext?.isValid) ||
								impersonationContext?.isValid
							}
							expirationDate={
								scheduledPost?.status !== Api.PostStatus.Draft ? context?.template?.schedule?.expirationDate : null
							}
							scheduleCtaText={scheduledPost?.status === PostStatus.Pending ? 'Okay' : 'Update Schedule'}
						/>
					</PrivateRoute>
					<PrivateRoute path={`${routeMatch.url}/send-options`} userSession={userSession}>
						<EditSocialMediaSendOptions
							selectedUser={context?.selectedCreator}
							sendOnBehalf={
								(context?.selectedCreator?.id !== userSession.user.id && !impersonationContext?.isValid) ||
								impersonationContext?.isValid
							}
							ignoreCompliance={bypassCompliance}
							initialStartDate={moment(
								postReport?.dueDate || postToEdit?.dueDate || locationStateCreateRequest?.schedule?.startDate
							)}
							onSubmitPost={onSubmitPost}
							className={css(styleSheet.sendOptions)}
							expirationDate={
								postToEdit?.status !== Api.PostStatus.Draft ? context?.template?.schedule?.expirationDate : null
							}
							templateId={routeMatch.params.templateId}
							isSchedulingInProgress={isSchedulingInProgress}
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: showingCancelConfirmation,
								onRequestClose: onCancelConfirmationRequestClose,
							}}
							options={CancelConfirmationOptions}
							title={`Are you sure you want to ${
								source?.status === Api.PostStatus.Draft ? 'delete' : 'cancel'
							} this social media ${source?.status === Api.PostStatus.Draft ? 'draft' : 'post'}?`}
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: showingCancelUpdateConfirmation,
								onRequestClose: onCancelUpdateConfirmationRequestClose,
							}}
							options={CancelUpdateConfirmationOptions}
							title='Are you sure you want to cancel these updates?'
						/>
					</PrivateRoute>
					<PrivateRoute exact={true} path={`${routeMatch.url}/compliance-complete`} userSession={userSession}>
						<ComplianceConfirmation onDismiss={fullscreenModal?.dismissModal} styles={[styleSheet.childRoute]} />
					</PrivateRoute>
					<PrivateRoute userSession={userSession} path={`${routeMatch.url}/schedule-complete`}>
						{userSession?.account?.features?.contentGeneration?.enabled ? (
							<AIScheduleSuccess
								resourceType='social'
								socialPost={scheduledPost}
								styles={[styleSheet.postCompleteContainer]}
								hideHeader={true}
							/>
						) : (
							<SocialMediaPostScheduleComplete post={scheduledPost} onClose={() => fullscreenModal.dismissModal()} />
						)}
					</PrivateRoute>
					<PrivateRoute userSession={userSession}>
						{onRenderComposer()}
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: showingCancelConfirmation,
								onRequestClose: onCancelConfirmationRequestClose,
							}}
							options={CancelConfirmationOptions}
							title={`Are you sure you want to ${
								source?.status === Api.PostStatus.Draft ? 'delete' : 'cancel'
							} this social media ${source?.status === Api.PostStatus.Draft ? 'draft' : 'post'}?`}
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: showingCancelUpdateConfirmation,
								onRequestClose: onCancelUpdateConfirmationRequestClose,
							}}
							options={CancelUpdateConfirmationOptions}
							title='Are you sure you want to cancel these updates?'
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: showingUpdateConfirmation,
								onRequestClose: onUpdateConfirmationRequestClose,
							}}
							options={UpdateConfirmationOptions}
							title='Are you sure you want to update this social media post?'
						/>
						{showSaveAsModal ? (
							<TextInputModal
								cta='Save'
								modalProps={{
									isOpen: showSaveAsModal,
									onRequestClose: onSaveAsDraftPost,
								}}
								placeholderText='Name'
								title='Save as a Draft'
								initialValue={context.campaignName}
							/>
						) : null}
						<Modal
							isOpen={Boolean(templateToBeSaved)}
							onRequestClose={() => setTemplateToBeSaved(null)}
							className={css(styleSheet.saveTemplateModal)}
							useDefaultHeader
							shouldCloseOnOverlayClick={false}
						>
							{templateToBeSaved ? (
								<SaveTemplate
									canImpersonateOnTemplateSave
									template={templateToBeSaved}
									onCancel={() => setTemplateToBeSaved(null)}
									onSuccess={onForkedTemplate}
									enableKeywords
								/>
							) : null}
						</Modal>
						{showingCitations ? (
							<div>
								<ViewCitationsModal modalProps={{ isOpen: showingCitations, onRequestClose: onCitationRequestClose }}>
									{context?.template?.citation}
								</ViewCitationsModal>
							</div>
						) : null}
					</PrivateRoute>
				</Switch>
			</EditSocialMediaPostContext.Provider>
			<FabContext appearance={{ hidden: true }} />
		</div>
	);
});

export const EditSocialMediaPostDesktop = inject(
	ImpersonationContextKey,
	ModalChildComponentContextKey
)(EditSocialMediaPostBase);

export const EditSocialMediaPost = (props: IProps) => {
	const isMobile = useMediaQuery({ maxWidth: THEME.breakpoints.md });

	if (isMobile) {
		return <MobileEditSocialMediaPostContainer {...props} />;
	}
	return <EditSocialMediaPostDesktop {...props} />;
};
